import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  FileMinus2,
  Loader2,
  MoreVertical,
  Plus,
  RefreshCw,
  Search,
  Send,
  Eye,
  Download,
  FileText,
  Euro,
  ExternalLink,
} from "lucide-react";
import { labelDestinatarioOspite } from "../utils/fatturazioneLabels";

function KpiCard({ icon: Icon, iconClass, title, value, sub }) {
  return (
    <Card className="card-base">
      <CardContent className="p-4 flex items-start gap-3">
        <span className={cn("inline-flex h-9 w-9 items-center justify-center rounded-lg shrink-0", iconClass)}>
          <Icon className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <p className="text-xs text-slate-500">{title}</p>
          <p className="text-lg font-semibold text-slate-900 tabular-nums">{value}</p>
          {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

const STATO_NC_UI = {
  bozza: { label: "Bozza", className: "bg-amber-100 text-amber-800" },
  generata: { label: "Da inviare", className: "bg-sky-100 text-sky-800" },
  approvata: { label: "Approvata", className: "bg-sky-100 text-sky-800" },
  in_coda_sdi: { label: "In coda SDI", className: "bg-sky-100 text-sky-800 ring-1 ring-sky-200" },
  inviata_sdi: { label: "Inviata SDI", className: "bg-emerald-100 text-emerald-800" },
  consegnata: { label: "Consegnata", className: "bg-emerald-100 text-emerald-800" },
  accettata: { label: "Accettata", className: "bg-emerald-100 text-emerald-800" },
  rifiutata: { label: "Rifiutata", className: "bg-red-100 text-red-800" },
  scartata: { label: "Scartata", className: "bg-red-100 text-red-800" },
  stornata: { label: "Stornata", className: "bg-slate-100 text-slate-700" },
};

function canCreateNcFromInvoice(f) {
  return (
    f?.tipo_documento !== "nota_credito" &&
    ["inviata_sdi", "consegnata", "accettata"].includes(f?.stato) &&
    !f?.nota_credito_id
  );
}

export default function FatturazioneNoteCreditoTab({
  noteCredito,
  fattureEligibiliNc,
  formatEuro,
  formatMese,
  formatDataIt,
  fascicoloLabel,
  loadingFatture,
  loadFatture,
  openDetail,
  handleInviaSdi,
  sendingSdi,
  canInviaSdi,
  handleDownloadXml,
  handleOpenFatturaPdf,
  handleCreaNotaCredito,
  creatingNotaCredito,
  handleSyncFicNoteCredito,
  syncFicNcLoading,
  isFic,
  filtroMese,
  setFiltroMese,
  mesiOptions,
  searchQuery,
  setSearchQuery,
}) {
  const [creaOpen, setCreaOpen] = useState(false);
  const [selectedFatturaId, setSelectedFatturaId] = useState("");
  const [creaSearch, setCreaSearch] = useState("");

  const filtered = useMemo(() => {
    let list = noteCredito || [];
    if (filtroMese && filtroMese !== "__tutti__") {
      list = list.filter((f) => (f.mese || String(f.data_emissione || "").slice(0, 7)) === filtroMese);
    }
    const q = (searchQuery || "").trim().toLowerCase();
    if (q) {
      list = list.filter((f) => {
        const ospite = fascicoloLabel?.(f.fascicolo_id) || "";
        return (
          (f.numero || "").toLowerCase().includes(q) ||
          (f.destinatario_nome || "").toLowerCase().includes(q) ||
          (f.fattura_riferimento_numero || "").toLowerCase().includes(q) ||
          ospite.toLowerCase().includes(q)
        );
      });
    }
    return list;
  }, [noteCredito, filtroMese, searchQuery, fascicoloLabel]);

  const eligibiliFiltrate = useMemo(() => {
    const q = creaSearch.trim().toLowerCase();
    const base = (fattureEligibiliNc || []).filter(canCreateNcFromInvoice);
    if (!q) return base;
    return base.filter((f) => {
      const ospite = fascicoloLabel?.(f.fascicolo_id) || "";
      return (
        (f.numero || "").toLowerCase().includes(q) ||
        (f.destinatario_nome || "").toLowerCase().includes(q) ||
        ospite.toLowerCase().includes(q)
      );
    });
  }, [fattureEligibiliNc, creaSearch, fascicoloLabel]);

  const kpi = useMemo(() => {
    const totale = filtered.reduce((a, f) => a + (Number(f.totale) || 0), 0);
    const bozze = filtered.filter((f) => ["bozza", "generata", "approvata"].includes(f.stato)).length;
    const inviate = filtered.filter((f) =>
      ["inviata_sdi", "consegnata", "accettata"].includes(f.stato),
    ).length;
    return { count: filtered.length, totale, bozze, inviate };
  }, [filtered]);

  const selectedFattura = useMemo(
    () => (fattureEligibiliNc || []).find((f) => f.id === selectedFatturaId) || null,
    [fattureEligibiliNc, selectedFatturaId],
  );

  const handleConfirmCrea = async () => {
    if (!selectedFattura) return;
    await handleCreaNotaCredito?.(selectedFattura);
    setCreaOpen(false);
    setSelectedFatturaId("");
    setCreaSearch("");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Note di credito</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Storno TD04 da fattura inviata: duplica le voci, riprende note/causale e numerazione dedicata
            (allineata a Fatture in Cloud)
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isFic && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSyncFicNoteCredito}
              disabled={loadingFatture || syncFicNcLoading}
            >
              {syncFicNcLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sync da FIC
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={loadFatture}
            disabled={loadingFatture}
          >
            {loadingFatture ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Aggiorna
          </Button>
          <Button
            type="button"
            size="sm"
            className="bg-purple-600 hover:bg-purple-700 text-white"
            onClick={() => setCreaOpen(true)}
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Crea nota da fattura
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiCard
          icon={FileMinus2}
          iconClass="bg-purple-100 text-purple-700"
          title="Note di credito"
          value={loadingFatture ? "…" : kpi.count}
          sub={filtroMese && filtroMese !== "__tutti__" ? formatMese(filtroMese) : "Tutti i periodi"}
        />
        <KpiCard
          icon={Euro}
          iconClass="bg-violet-100 text-violet-700"
          title="Importo totale"
          value={loadingFatture ? "…" : formatEuro(kpi.totale)}
        />
        <KpiCard
          icon={FileText}
          iconClass="bg-emerald-100 text-emerald-700"
          title="Inviate / in bozza"
          value={loadingFatture ? "…" : `${kpi.inviate} / ${kpi.bozze}`}
          sub="Inviate SDI · Da completare"
        />
      </div>

      <Card className="card-base overflow-hidden">
        <div className="flex flex-col gap-3 p-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                className="pl-9 bg-white"
                placeholder="Cerca numero NC, fattura rif., ospite…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={filtroMese || "__tutti__"} onValueChange={setFiltroMese}>
              <SelectTrigger className="w-[180px] bg-white h-9 text-sm">
                <SelectValue placeholder="Mese" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__tutti__">Tutti i mesi</SelectItem>
                {(mesiOptions || []).filter(Boolean).map((m) => (
                  <SelectItem key={m} value={m}>
                    {formatMese(m)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loadingFatture && filtered.length === 0 ? (
          <div className="py-16 text-center text-slate-500">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            Caricamento…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 px-6 text-center space-y-3">
            <p className="text-sm text-slate-500">Nessuna nota di credito con i filtri selezionati.</p>
            <Button type="button" size="sm" variant="outline" onClick={() => setCreaOpen(true)}>
              <Plus className="w-4 h-4 mr-1.5" />
              Crea nota da fattura
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-slate-500 font-medium">Numero NC</TableHead>
                <TableHead className="text-slate-500 font-medium">Data</TableHead>
                <TableHead className="text-slate-500 font-medium">Destinatario</TableHead>
                <TableHead className="text-slate-500 font-medium">Fattura rif.</TableHead>
                <TableHead className="text-slate-500 font-medium text-right">Totale</TableHead>
                <TableHead className="text-slate-500 font-medium">Stato</TableHead>
                <TableHead className="text-slate-500 font-medium text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((f) => {
                const ospite = fascicoloLabel?.(f.fascicolo_id) || "";
                const label = labelDestinatarioOspite({
                  destinatarioNome: f.destinatario_nome,
                  ospiteNome: ospite,
                });
                const inCodaSdi = Boolean(f.sdi_coda_at);
                const statoUi = inCodaSdi
                  ? STATO_NC_UI.in_coda_sdi
                  : STATO_NC_UI[f.stato] || {
                      label: f.stato || "—",
                      className: "bg-slate-100 text-slate-700",
                    };
                const canSend =
                  !inCodaSdi && ["bozza", "generata", "approvata"].includes(f.stato);
                return (
                  <TableRow key={f.id}>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => openDetail(f.id)}
                        className="inline-flex items-center gap-1.5 font-mono text-sm text-purple-700 hover:underline"
                      >
                        <FileMinus2 className="w-3.5 h-3.5" />
                        {f.numero}
                      </button>
                      {f.origine_documento === "fic_collegata" && (
                        <Badge className="ml-2 text-[10px] bg-sky-100 text-sky-800">Da FIC</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-700">{formatDataIt(f.data_emissione)}</TableCell>
                    <TableCell>
                      <p className="font-medium text-slate-800 truncate max-w-[200px]" title={label.full}>
                        {label.primary}
                      </p>
                      {label.secondary && (
                        <p className="text-xs text-slate-500 truncate max-w-[200px]">({label.secondary})</p>
                      )}
                    </TableCell>
                    <TableCell>
                      {f.fattura_riferimento_numero ? (
                        <button
                          type="button"
                          className="text-sm text-blue-700 hover:underline font-mono"
                          onClick={() =>
                            f.fattura_riferimento_id
                              ? openDetail(f.fattura_riferimento_id)
                              : undefined
                          }
                          disabled={!f.fattura_riferimento_id}
                        >
                          {f.fattura_riferimento_numero}
                        </button>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {formatEuro(f.totale)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={cn(
                          "text-[10px] font-normal inline-flex items-center gap-1",
                          statoUi.className,
                        )}
                        title={inCodaSdi ? "Invio allo SDI in corso in background" : undefined}
                      >
                        {inCodaSdi && <Loader2 className="w-3 h-3 animate-spin" aria-hidden />}
                        {statoUi.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
                          title="Dettaglio"
                          onClick={() => openDetail(f.id)}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {canSend && (
                          <button
                            type="button"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-emerald-600 hover:bg-emerald-50 disabled:opacity-40"
                            title={canInviaSdi ? "Invia a SDI (TD04)" : "Configura fatturazione"}
                            disabled={sendingSdi || !canInviaSdi}
                            onClick={() => handleInviaSdi(f.id)}
                          >
                            {sendingSdi ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
                              title="Altre azioni"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => openDetail(f.id)}>
                              Dettaglio
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadXml(f.id, f.numero)}>
                              <Download className="w-3.5 h-3.5 mr-2" />
                              Scarica XML
                            </DropdownMenuItem>
                            {f.voci?.length > 0 && (
                              <DropdownMenuItem onClick={() => handleOpenFatturaPdf(f)}>
                                Apri PDF
                              </DropdownMenuItem>
                            )}
                            {f.fascicolo_id && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem asChild>
                                  <Link
                                    to={`/fascicoli-assistenziali/${f.fascicolo_id}?tab=amministrativa`}
                                  >
                                    Apri fascicolo
                                    <ExternalLink className="w-3.5 h-3.5 ml-auto opacity-60" />
                                  </Link>
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog
        open={creaOpen}
        onOpenChange={(open) => {
          setCreaOpen(open);
          if (!open) {
            setSelectedFatturaId("");
            setCreaSearch("");
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Crea nota di credito da fattura</DialogTitle>
            <DialogDescription>
              Seleziona una fattura già inviata allo SDI. Affidya duplica le voci (TD04), riprende
              note/causale e assegna la numerazione note di credito (sincronizzata con FIC).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-xs text-slate-500">Cerca fattura</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  className="pl-9"
                  placeholder="Numero, ospite, destinatario…"
                  value={creaSearch}
                  onChange={(e) => setCreaSearch(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Fattura da stornare</Label>
              <Select value={selectedFatturaId} onValueChange={setSelectedFatturaId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona fattura inviata…" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {eligibiliFiltrate.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-slate-500">
                      Nessuna fattura eleggibile (serve inviata a SDI e senza NC già collegata).
                    </div>
                  ) : (
                    eligibiliFiltrate.map((f) => {
                      const ospite = fascicoloLabel?.(f.fascicolo_id) || f.destinatario_nome || "—";
                      return (
                        <SelectItem key={f.id} value={f.id}>
                          {f.numero} · {ospite} · {formatEuro(f.totale)}
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            </div>
            {selectedFattura && (
              <div className="rounded-lg border border-purple-100 bg-purple-50/60 px-3 py-2 text-xs text-slate-700 space-y-1">
                <p>
                  <strong>Periodo:</strong> {formatMese(selectedFattura.mese) || "—"}
                </p>
                <p>
                  <strong>Destinatario:</strong> {selectedFattura.destinatario_nome || "—"}
                </p>
                {(selectedFattura.note || selectedFattura.causale) && (
                  <p className="line-clamp-2">
                    <strong>Note/causale:</strong>{" "}
                    {selectedFattura.note || selectedFattura.causale}
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreaOpen(false)}>
              Annulla
            </Button>
            <Button
              type="button"
              className="bg-purple-600 hover:bg-purple-700 text-white"
              disabled={!selectedFatturaId || creatingNotaCredito === selectedFatturaId}
              onClick={handleConfirmCrea}
            >
              {creatingNotaCredito === selectedFatturaId ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creazione…
                </>
              ) : (
                <>
                  <FileMinus2 className="w-4 h-4 mr-2" />
                  Crea nota di credito
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
